import { FormEvent, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { DraggableModalPanel } from '../ui/DraggableModalPanel';
import { FieldLabel } from '../ui/FieldLabel';
import { FormActionBar } from '../ui/FormActionBar';
import {
  Contract,
  ContractUpsertPayload,
  updateContract,
} from '../../services/commercialContractsApi';
import { EmailTemplate, listEmailTemplates } from '../../services/emailTemplatesApi';
import { listSurveyForms, SurveyForm } from '../../services/customerSuccessEngagementApi';
import { matchesTemplateKind } from '../../utils/matchesTemplateKind';
import { matchesSurveyType } from '../../utils/matchesSurveyType';
import { getRequestErrorMessage } from '../../utils/getRequestErrorMessage';
import { contractFieldHelp } from '../../pages/commercial/contractFieldHelp';

const NPS_FREQUENCY_OPTIONS = [
  { value: '', label: 'Padrão das configurações' },
  { value: 'Daily', label: 'Diário' },
  { value: 'Weekly', label: 'Semanal' },
  { value: 'Monthly', label: 'Mensal' },
  { value: 'Yearly', label: 'Anual' },
];

type EngagementFormState = {
  welcomeEnabled: boolean;
  welcomeEmailTemplateId: string | null;
  csatEnabled: boolean;
  csatSurveyFormId: string | null;
  npsEnabled: boolean;
  npsSurveyFormId: string | null;
  npsFrequency: string | null;
};

type ContractEngagementModalProps = {
  open: boolean;
  contract: Contract | null;
  canWrite: boolean;
  onClose: () => void;
  onSaved: (contract: Contract) => void;
};

function toDateInputValue(value: string): string {
  return value.slice(0, 10);
}

function engagementFromContract(contract: Contract): EngagementFormState {
  return {
    welcomeEnabled: contract.welcomeEnabled ?? true,
    welcomeEmailTemplateId: contract.welcomeEmailTemplateId ?? null,
    csatEnabled: contract.csatEnabled ?? true,
    csatSurveyFormId: contract.csatSurveyFormId ?? null,
    npsEnabled: contract.npsEnabled ?? true,
    npsSurveyFormId: contract.npsSurveyFormId ?? null,
    npsFrequency: contract.npsFrequency ?? null,
  };
}

function toUpsertPayload(contract: Contract, engagement: EngagementFormState): ContractUpsertPayload {
  return {
    code: contract.code,
    title: contract.title,
    description: contract.description ?? '',
    companyId: contract.companyId,
    salesProductId: contract.salesProductId ?? undefined,
    status: contract.status,
    startDateUtc: new Date(`${toDateInputValue(contract.startDateUtc)}T00:00:00.000Z`).toISOString(),
    endDateUtc: new Date(`${toDateInputValue(contract.endDateUtc)}T23:59:59.000Z`).toISOString(),
    responseSlaId: contract.responseSlaId ?? undefined,
    resolutionSlaId: contract.resolutionSlaId ?? undefined,
    calendarId: contract.calendarId ?? undefined,
    ownerUserId: contract.ownerUserId ?? undefined,
    amount: contract.amount ?? undefined,
    notes: contract.notes ?? '',
    welcomeEnabled: engagement.welcomeEnabled,
    welcomeEmailTemplateId: engagement.welcomeEmailTemplateId,
    csatEnabled: engagement.csatEnabled,
    csatSurveyFormId: engagement.csatSurveyFormId,
    npsEnabled: engagement.npsEnabled,
    npsSurveyFormId: engagement.npsSurveyFormId,
    npsFrequency: engagement.npsFrequency,
  };
}

function formatSurveyFormOption(item: SurveyForm): string {
  if (!item.companies?.length) {
    return `${item.name} · Padrão`;
  }

  return `${item.name} · ${item.companies.map((company) => company.name).join(', ')}`;
}

export function ContractEngagementModal({
  open,
  contract,
  canWrite,
  onClose,
  onSaved,
}: ContractEngagementModalProps) {
  const [form, setForm] = useState<EngagementFormState | null>(null);
  const [saving, setSaving] = useState(false);
  const [loadingLookups, setLoadingLookups] = useState(false);
  const [emailTemplates, setEmailTemplates] = useState<EmailTemplate[]>([]);
  const [surveyForms, setSurveyForms] = useState<SurveyForm[]>([]);

  useEffect(() => {
    if (open && contract) {
      setForm(engagementFromContract(contract));
    } else {
      setForm(null);
    }
  }, [open, contract]);

  useEffect(() => {
    if (!open) {
      return;
    }

    let cancelled = false;
    setLoadingLookups(true);

    void (async () => {
      try {
        const [templates, forms] = await Promise.all([
          listEmailTemplates('customer-success'),
          listSurveyForms(),
        ]);
        if (cancelled) {
          return;
        }

        setEmailTemplates(templates.filter((item) => item.isActive));
        setSurveyForms(forms.filter((item) => item.isActive));
      } catch (error) {
        if (!cancelled) {
          toast.error(getRequestErrorMessage(error, 'Falha ao carregar templates e formulários.'));
          setEmailTemplates([]);
          setSurveyForms([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingLookups(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open]);

  const welcomeTemplates = useMemo(
    () => emailTemplates.filter((item) => matchesTemplateKind(item.kind, 'welcome')),
    [emailTemplates],
  );
  const csatForms = useMemo(
    () => surveyForms.filter((item) => matchesSurveyType(item.surveyType, 'csat')),
    [surveyForms],
  );
  const npsForms = useMemo(
    () => surveyForms.filter((item) => matchesSurveyType(item.surveyType, 'nps')),
    [surveyForms],
  );

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!canWrite || !contract || !form) {
      return;
    }

    setSaving(true);
    try {
      const updated = await updateContract(contract.id, toUpsertPayload(contract, form));
      toast.success('Engajamento atualizado.');
      onSaved(updated);
      onClose();
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha ao salvar engajamento.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <DraggableModalPanel
      open={open && contract != null && form != null}
      title="Engajamento"
      subtitle={contract ? `${contract.contractCode} · ${contract.title}` : undefined}
      onClose={onClose}
      className="w-full max-w-2xl"
      closeDisabled={saving}
      footer={
        <FormActionBar onCancel={onClose} cancelLabel="Cancelar" className="erp-btn-actions justify-end">
          {canWrite ? (
            <button
              type="submit"
              form="contract-engagement-form"
              className="erp-btn-primary"
              disabled={saving || loadingLookups}
            >
              {saving ? 'Salvando...' : 'Salvar'}
            </button>
          ) : null}
        </FormActionBar>
      }
    >
      {form ? (
        <form id="contract-engagement-form" onSubmit={(event) => void handleSubmit(event)} className="space-y-4">
          <p className="text-xs text-secondary-500">
            Ligado = envia. Formulário vazio usa canal e, se não houver, o padrão da organização. O
            e-mail CSAT/NPS vem do formulário.
          </p>

          <div className="grid gap-3 sm:grid-cols-2">
            <label className="flex items-center gap-2 text-sm sm:col-span-2">
              <input
                type="checkbox"
                checked={form.welcomeEnabled}
                disabled={!canWrite}
                onChange={(e) => setForm({ ...form, welcomeEnabled: e.target.checked })}
              />
              <FieldLabel {...contractFieldHelp.welcomeEnabled}>Enviar boas-vindas</FieldLabel>
            </label>
            <label className="erp-label block sm:col-span-2">
              <FieldLabel {...contractFieldHelp.welcomeEmailTemplate}>Template boas-vindas</FieldLabel>
              <select
                className="erp-input mt-1 w-full"
                disabled={!canWrite || !form.welcomeEnabled || loadingLookups}
                value={form.welcomeEmailTemplateId ?? ''}
                onChange={(e) =>
                  setForm({ ...form, welcomeEmailTemplateId: e.target.value || null })
                }
              >
                <option value="">Padrão das configurações</option>
                {welcomeTemplates.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </select>
            </label>

            <label className="flex items-center gap-2 text-sm sm:col-span-2">
              <input
                type="checkbox"
                checked={form.csatEnabled}
                disabled={!canWrite}
                onChange={(e) => setForm({ ...form, csatEnabled: e.target.checked })}
              />
              <FieldLabel {...contractFieldHelp.csatEnabled}>Enviar CSAT</FieldLabel>
            </label>
            <label className="erp-label block sm:col-span-2">
              <FieldLabel {...contractFieldHelp.csatSurveyForm}>Formulário CSAT</FieldLabel>
              <select
                className="erp-input mt-1 w-full"
                disabled={!canWrite || !form.csatEnabled || loadingLookups}
                value={form.csatSurveyFormId ?? ''}
                onChange={(e) => setForm({ ...form, csatSurveyFormId: e.target.value || null })}
              >
                <option value="">Padrão (canal / organização)</option>
                {csatForms.map((item) => (
                  <option key={item.id} value={item.id}>
                    {formatSurveyFormOption(item)}
                  </option>
                ))}
              </select>
              {!loadingLookups && csatForms.length === 0 ? (
                <span className="erp-hint mt-1 block">
                  Nenhum formulário CSAT ativo.{' '}
                  <Link to="/customer-success/survey-forms" className="underline">
                    Cadastre em Formulários
                  </Link>
                  .
                </span>
              ) : null}
            </label>

            <label className="flex items-center gap-2 text-sm sm:col-span-2">
              <input
                type="checkbox"
                checked={form.npsEnabled}
                disabled={!canWrite}
                onChange={(e) => setForm({ ...form, npsEnabled: e.target.checked })}
              />
              <FieldLabel {...contractFieldHelp.npsEnabled}>Enviar NPS</FieldLabel>
            </label>
            <label className="erp-label block">
              <FieldLabel {...contractFieldHelp.npsSurveyForm}>Formulário NPS</FieldLabel>
              <select
                className="erp-input mt-1 w-full"
                disabled={!canWrite || !form.npsEnabled || loadingLookups}
                value={form.npsSurveyFormId ?? ''}
                onChange={(e) => setForm({ ...form, npsSurveyFormId: e.target.value || null })}
              >
                <option value="">
                  {loadingLookups ? 'Carregando...' : 'Padrão (canal / organização)'}
                </option>
                {npsForms.map((item) => (
                  <option key={item.id} value={item.id}>
                    {formatSurveyFormOption(item)}
                  </option>
                ))}
              </select>
              {!loadingLookups && npsForms.length === 0 ? (
                <span className="erp-hint mt-1 block">
                  Nenhum formulário NPS ativo.{' '}
                  <Link to="/customer-success/survey-forms" className="underline">
                    Cadastre em Formulários
                  </Link>{' '}
                  (tipo NPS).
                </span>
              ) : null}
            </label>
            <label className="erp-label block">
              <FieldLabel {...contractFieldHelp.npsFrequency}>Periodicidade NPS</FieldLabel>
              <select
                className="erp-input mt-1 w-full"
                disabled={!canWrite || !form.npsEnabled}
                value={form.npsFrequency ?? ''}
                onChange={(e) => setForm({ ...form, npsFrequency: e.target.value || null })}
              >
                {NPS_FREQUENCY_OPTIONS.map((option) => (
                  <option key={option.value || 'default'} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </form>
      ) : null}
    </DraggableModalPanel>
  );
}
